import { useEffect, useState } from "react";
import type { CasoDetalle, PaqueteOut } from "@/lib/api";
import { useAuth, canEdit } from "@/lib/auth";
import ConfirmDialog from "@/ui/ConfirmDialog";
import { useToast } from "@/ui/Toast";
import { leyendaEliminacion } from "@/lib/retencion";
import { fmtBytes } from "@/lib/formato";
import { copiarTexto } from "@/lib/copiar";
import { fmtFechaCarga, fmtFechaCargaLarga } from "@/lib/fechas";

// El panel se pliega (pedido de Joe, 2026-08-21) y recuerda cómo lo dejaste,
// con la misma convención `plegable:` de las secciones del compositor de correo.
const PLEGABLE_KEY = "plegable:paquetes";

// Paquetes ZIP del caso: listar, copiar sus datos y eliminarlos antes de la
// retención. Vivía dentro de TabEvidencias.tsx (1,174 líneas); se sacó
// íntegro el 2026-08-17.
export default function PaquetesPanel({
  caso,
  refreshKey,
  ticketExterno,
}: {
  caso: CasoDetalle;
  refreshKey: number;
  ticketExterno: string | null;
}) {
  const toast = useToast();
  const me = useAuth((s) => s.user);
  const puedeEditar = canEdit(me);
  const [paquetes, setPaquetes] = useState<PaqueteOut[]>([]);
  const [loading, setLoading] = useState(true);
  const [confirmDel, setConfirmDel] = useState<PaqueteOut | null>(
    null,
  );
  const [abierto, setAbierto] = useState<boolean>(() => {
    try {
      return localStorage.getItem(PLEGABLE_KEY) !== "0";
    } catch {
      return true;
    }
  });

  function alternar() {
    setAbierto((v) => {
      try {
        localStorage.setItem(PLEGABLE_KEY, v ? "0" : "1");
      } catch {
        // sin localStorage (modo privado estricto): solo no recuerda
      }
      return !v;
    });
  }

  async function load() {
    setLoading(true);
    try {
      const res = await fetch(`/api/casos/${caso.id}/paquetes`, { credentials: "include" });
      if (!res.ok) throw new Error(res.statusText);
      setPaquetes(await res.json());
    } catch {
      // noop, error de red
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caso.id, refreshKey]);

  // Se acaba de generar un ZIP (refreshKey sube): el panel se abre solo aunque
  // lo hayas dejado plegado — si no, el paquete nuevo quedaba escondido sin
  // que te dieras cuenta. No toca la preferencia guardada.
  useEffect(() => {
    if (refreshKey > 0) setAbierto(true);
  }, [refreshKey]);

  async function copiar(text: string, label: string) {
    if (await copiarTexto(text)) toast.success(`${label} copiado`);
    else toast.error("No se pudo copiar");
  }

  async function eliminarPaquete(p: PaqueteOut) {
    try {
      const res = await fetch(`/api/paquetes/${p.id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) throw new Error((await res.json()).detail || res.statusText);
      toast.success("Paquete eliminado", p.nombre_zip);
      await load();
    } catch (err) {
      toast.error("No se pudo eliminar", String(err));
    }
  }

  if (loading) return null;
  if (paquetes.length === 0) return null;

  // Si todos los paquetes comparten el mismo password (el patron normal —
  // ultimos 6 del TE), lo mostramos UNA vez arriba en lugar de repetirlo
  // en cada fila. Si un paquete tiene password distinto (legacy o override),
  // ese si se muestra inline para evitar confusion.
  const passwords = new Set(paquetes.map((p) => p.password));
  const passwordUnico = passwords.size === 1 ? paquetes[0].password : null;

  // El más reciente, para el resumen de la cabecera plegada.
  const ultimo = paquetes.reduce((a, b) =>
    new Date(b.created_at).getTime() > new Date(a.created_at).getTime() ? b : a,
  );

  return (
    <div className={abierto ? "card space-y-3" : "card"}>
      {/* Patrón disclosure (WAI-ARIA APG): el encabezado envuelve al botón,
          no al revés — un <h3> dentro de <button> es HTML inválido. */}
      <h3 className="text-sm font-medium text-zinc-300">
        <button
          type="button"
          onClick={alternar}
          aria-expanded={abierto}
          className="w-full flex flex-wrap items-center gap-x-2 gap-y-0.5 text-left group"
        >
          <span className="text-[10px] text-zinc-500 w-3 flex-shrink-0">
            {abierto ? "▾" : "▸"}
          </span>
          <span className="group-hover:text-zinc-100 transition flex-shrink-0">
            Paquetes generados ({paquetes.length})
          </span>
          {/* En móvil el resumen baja a su propio renglón (basis-full + order-last):
              en línea se truncaba y la contraseña quedaba en «233…». */}
          {!abierto && (
            <span className="text-[11px] font-normal text-zinc-500 truncate min-w-0 basis-full pl-5 order-last sm:basis-auto sm:pl-0 sm:order-none">
              {/* La contraseña va primero: es lo que más se copia de aquí y
                  plegado no debe esconderla (el plegado se recuerda en TODOS
                  los casos). */}
              {passwordUnico && (
                <>
                  · contraseña{" "}
                  <span className="font-mono text-accent-300">{passwordUnico}</span>{" "}
                </>
              )}
              · último: <span className="font-mono">{ultimo.nombre_zip}</span> ·{" "}
              {fmtFechaCarga(ultimo.created_at)}
            </span>
          )}
          <span className="ml-auto text-[10px] font-normal text-zinc-600 flex-shrink-0">
            {abierto ? "ocultar" : "mostrar"}
          </span>
        </button>
      </h3>

      {abierto && passwordUnico && (
        <div className="rounded border border-accent-500/30 bg-accent-500/5 p-3 flex items-center gap-3 flex-wrap">
          <div className="text-xs text-zinc-400">
            <span className="uppercase tracking-wide">Contraseña de este caso</span>
            {ticketExterno && (
              <span className="text-zinc-500"> · últimos 6 del TE {ticketExterno}</span>
            )}
          </div>
          <code className="bg-ink-800 px-3 py-1 rounded font-mono text-base text-accent-300">
            {passwordUnico}
          </code>
          <button
            onClick={() => copiar(passwordUnico, "Contraseña")}
            className="btn-ghost text-xs"
          >
            Copiar
          </button>
          <span className="text-xs text-zinc-500 ml-auto">
            Aplica a todos los ZIPs de abajo. Si ya se lo diste al cliente antes, ya lo conoce.
          </span>
        </div>
      )}

      {abierto && (
      <ul className="divide-y divide-ink-700">
        {paquetes.map((p) => {
          const expirado = new Date(p.expira_at).getTime() < Date.now();
          const passwordDistinto = passwordUnico === null;
          const zipEliminado = p.archivo_eliminado_at !== null;
          const zipRetencion =
            !zipEliminado && p.borrar_archivo_at
              ? leyendaEliminacion(p.borrar_archivo_at)
              : null;
          return (
            <li
              key={p.id}
              className={`py-3 space-y-1 ${expirado || zipEliminado ? "opacity-50" : ""}`}
            >
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <span className="font-mono text-sm text-accent-400">{p.nombre_zip}</span>
                {/* Cada dato va en su propio nowrap: en móvil la línea envuelve
                    entre datos, nunca a media frase ("0 / descargas"). */}
                <span className="text-xs text-zinc-500">
                  <span className="whitespace-nowrap">
                    Generado {fmtFechaCargaLarga(p.created_at)}
                  </span>
                  {" · "}
                  <span className="whitespace-nowrap">{fmtBytes(p.size_bytes)}</span>
                  {" · "}
                  <span className="whitespace-nowrap">
                    {p.intentos_count} descargas
                    {p.descargado_at && " ✓"}
                  </span>
                  {expirado && " · EXPIRADO"}
                </span>
              </div>
              <div className="text-[10px]">
                {zipEliminado ? (
                  <span className="text-red-400/70">
                    ZIP eliminado por retención · se conserva el registro
                  </span>
                ) : zipRetencion ? (
                  <span className={zipRetencion.urgente ? "text-amber-400" : "text-zinc-600"}>
                    {zipRetencion.texto} (12 meses tras el cierre del caso)
                  </span>
                ) : (
                  <span className="text-zinc-700">
                    El ZIP se conserva mientras el caso siga abierto
                  </span>
                )}
              </div>
              <div className="flex flex-col gap-2 text-xs sm:flex-row sm:items-center">
                <input
                  readOnly
                  value={p.url_publica}
                  className="input flex-1 font-mono text-xs"
                />
                <button onClick={() => copiar(p.url_publica, "Enlace")} className="btn-ghost text-xs w-full sm:w-auto">
                  Copiar enlace
                </button>
                {puedeEditar && (
                  <button
                    onClick={() => setConfirmDel(p)}
                    className="btn-ghost text-xs text-red-400 hover:text-red-300 w-full sm:w-auto"
                  >
                    🗑 Eliminar
                  </button>
                )}
              </div>
              {passwordDistinto && (
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  {p.password ? (
                    <>
                      <span className="text-zinc-500">Contraseña (anterior):</span>
                      <code className="bg-ink-800 px-2 py-0.5 rounded font-mono">{p.password}</code>
                      <button onClick={() => copiar(p.password, "Contraseña")} className="btn-ghost text-xs">
                        Copiar
                      </button>
                    </>
                  ) : (
                    <span className="text-zinc-500">Sin contraseña (ZIP plano)</span>
                  )}
                  <span className="text-zinc-600 w-full sm:w-auto sm:ml-auto">
                    Expira {new Date(p.expira_at).toLocaleDateString()}
                  </span>
                </div>
              )}
              {!passwordDistinto && (
                <div className="text-[10px] text-zinc-600 text-right">
                  Expira {new Date(p.expira_at).toLocaleDateString()}
                </div>
              )}
            </li>
          );
        })}
      </ul>
      )}

      <ConfirmDialog
        open={confirmDel !== null}
        onOpenChange={(v) => !v && setConfirmDel(null)}
        title="¿Eliminar este paquete ZIP?"
        description={
          confirmDel
            ? `${confirmDel.nombre_zip} — se borra el archivo y su enlace de descarga deja de funcionar de inmediato. No se puede deshacer.` +
              (confirmDel.descargado_at
                ? ` Ojo: ya se descargó ${confirmDel.intentos_count} ${confirmDel.intentos_count === 1 ? "vez" : "veces"} — puede que ya lo tengan.`
                : "")
            : ""
        }
        confirmLabel="Eliminar"
        cancelLabel="Cancelar"
        variant="danger"
        onConfirm={() => {
          if (confirmDel) return eliminarPaquete(confirmDel);
        }}
      />
    </div>
  );
}
